import React, { useEffect, useRef } from 'react';
import {
  Copy,
  Scissors,
  Clipboard,
  Trash2,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  MoveUp,
  MoveDown,
  ArrowUp,
  ArrowDown,
  Edit3,
  Layers,
  Settings,
  Download,
  Upload,
  Move
} from 'lucide-react';

const ContextMenu = ({ 
  isVisible, 
  position, 
  onClose, 
  menuItems, 
  contextType = 'canvas' // 'canvas', 'item', 'timeline'
}) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  const getIcon = (iconName) => {
    const icons = {
      copy: Copy,
      cut: Scissors,
      paste: Clipboard,
      delete: Trash2,
      rotate: RotateCw,
      flipH: FlipHorizontal,
      flipV: FlipVertical,
      lock: Lock,
      unlock: Unlock,
      hide: EyeOff,
      show: Eye,
      bringForward: MoveUp,
      sendBackward: MoveDown,
      bringToFront: ArrowUp,
      sendToBack: ArrowDown,
      edit: Edit3,
      duplicate: Copy,
      properties: Settings,
      layers: Layers,
      download: Download,
      upload: Upload,
      move: Move
    };
    
    const IconComponent = icons[iconName];
    return IconComponent ? <IconComponent className="w-4 h-4" /> : null;
  };

  if (!isVisible) return null;

  return (
    <div
      ref={menuRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-[9999] min-w-48"
      style={{
        left: position.x,
        top: position.y,
        maxHeight: '400px',
        overflowY: 'auto'
      }}
    >
      {menuItems.map((item, index) => {
        if (item.type === 'separator') {
          return <div key={index} className="border-t border-gray-200 my-1" />;
        }

        return (
          <button
            key={index}
            onClick={() => {
              item.action();
              onClose();
            }}
            disabled={item.disabled}
            className={`
              w-full px-4 py-2 text-left text-sm flex items-center space-x-3 transition-colors
              ${item.disabled 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-700 hover:bg-gray-100 cursor-pointer'
              }
              ${item.danger ? 'hover:bg-red-50 hover:text-red-600' : ''}
            `}
          >
            {item.icon && (
              <span className="flex-shrink-0">
                {getIcon(item.icon)}
              </span>
            )}
            <span className="flex-1">{item.label}</span>
            {item.shortcut && (
              <span className="text-xs text-gray-400 ml-auto">
                {item.shortcut}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ContextMenu;
